import type Geometry from '@arcgis/core/geometry/Geometry.js';
import { resolve, type Source, SourceSchema } from '@/agent/refs';
import {
  ensureFeatureLayer,
  requireLayerRecord,
  resolveFieldName,
} from './shared/layers';
import type { Tool } from './types';

type StatsOp = 'count' | 'sum' | 'avg';
type Predicate = 'contains' | 'intersects';

type Input = {
  left: Source;
  right_layer_id: string;
  stats_op?: StatsOp;
  stats_field?: string;
  where?: string;
  predicate?: Predicate;
};

const LEFT_CAP = 500;

export const spatialJoin: Tool<Input> = {
  name: 'spatial_join',
  description:
    'For each feature in `left` (typically polygons), query `right_layer_id` for features that satisfy `predicate` (default `intersects`) and aggregate. `stats_op` ∈ count|sum|avg (default count). Returns rows of { left_objectid, value, n }. Caps left features at 500; flags `truncated: true` when more were skipped.',
  input_schema: {
    type: 'object',
    properties: {
      left: SourceSchema,
      right_layer_id: { type: 'string' },
      stats_op: { enum: ['count', 'sum', 'avg'] },
      stats_field: { type: 'string' },
      where: { type: 'string' },
      predicate: { enum: ['contains', 'intersects'] },
    },
    required: ['left', 'right_layer_id'],
    additionalProperties: false,
  },
  execute: async (input) => {
    const op: StatsOp = input.stats_op ?? 'count';
    const predicate: Predicate = input.predicate ?? 'intersects';

    const refLeft = await resolve(input.left);
    const leftFeatures: { objectid: number; geometry: Geometry }[] = [];

    if (refLeft.feature_set) {
      const fs = refLeft.feature_set;
      const n = Math.min(fs.objectids.length, fs.geometries.length);
      for (let i = 0; i < n; i++) {
        leftFeatures.push({ objectid: fs.objectids[i], geometry: fs.geometries[i] });
      }
    } else if (refLeft.feature) {
      leftFeatures.push({
        objectid: refLeft.feature.objectid,
        geometry: refLeft.feature.geometry,
      });
    } else if (refLeft.geometry) {
      leftFeatures.push({ objectid: 0, geometry: refLeft.geometry });
    } else {
      throw new Error('spatial_join: left source has no geometry');
    }

    if (leftFeatures.length === 0) {
      return { rows: [], scanned: 0, total: 0 };
    }

    const total = leftFeatures.length;
    const truncated = total > LEFT_CAP;
    const slice = truncated ? leftFeatures.slice(0, LEFT_CAP) : leftFeatures;

    const layer = await ensureFeatureLayer(input.right_layer_id);
    const probe = requireLayerRecord(input.right_layer_id).probe;
    const statsField = input.stats_field
      ? resolveFieldName(input.stats_field, probe, layer)
      : undefined;

    if ((op === 'sum' || op === 'avg') && !statsField) {
      throw new Error(
        `spatial_join: stats_op="${op}" requires a resolvable stats_field on layer "${input.right_layer_id}"`,
      );
    }

    const where = input.where && input.where.trim() ? input.where : '1=1';

    const rows = await Promise.all(
      slice.map(async (lf) => {
        if (op === 'count') {
          const value = await layer.queryFeatureCount({
            where,
            geometry: lf.geometry,
            spatialRelationship: predicate,
          });
          return { left_objectid: lf.objectid, value, n: value };
        }

        const rs = await layer.queryFeatures({
          where,
          outFields: statsField ? [statsField] : [],
          returnGeometry: false,
          geometry: lf.geometry,
          spatialRelationship: predicate,
        });
        const values: number[] = [];
        for (const f of rs.features) {
          const raw = f.attributes?.[statsField!];
          const num = typeof raw === 'number' ? raw : Number(raw);
          if (Number.isFinite(num)) values.push(num);
        }
        const sum = values.reduce((a, b) => a + b, 0);
        const value = op === 'sum' ? sum : values.length > 0 ? sum / values.length : 0;
        return { left_objectid: lf.objectid, value, n: values.length };
      }),
    );

    return {
      rows,
      scanned: slice.length,
      total,
      ...(truncated ? { truncated: true } : {}),
      stats_op: op,
      stats_field: statsField,
    };
  },
};
